import React from "react";

/**
 * App-weiter ErrorBoundary — letzte Verteidigungslinie.
 *
 * Fängt alle React-Render-Fehler ab, die durch die Sektions-lokalen
 * `ViewerErrorBoundary`s durchrutschen (z. B. wenn ein Nicht-Viewer-Component
 * crasht: State-Serialisierung, Context-Provider, Router-State etc.).
 *
 * Statt weißer Seite zeigt der User eine freundliche Fehlermeldung mit
 * „Seite neu laden"-Button. Der Fehler wird zusätzlich in die Browser-Console
 * geloggt (kein Analytics-Beacon — der User hat keine Consent-freien
 * Reporting-Rechte).
 */

type Props = { children: React.ReactNode };
type State = { hasError: boolean; message: string | null };

export default class AppErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false, message: null };

    static getDerivedStateFromError(error: unknown): State {
        const message =
            error instanceof Error ? error.message : "Unbekannter Fehler";
        return { hasError: true, message };
    }

    componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
        console.error("[AppErrorBoundary]", error, errorInfo);
    }

    private handleReload = () => {
        try {
            window.location.reload();
        } catch {
            /* ignore */
        }
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div
                data-testid="app-error-boundary-fallback"
                className="min-h-[70vh] flex items-center justify-center px-6 py-16"
                style={{ background: "hsl(var(--hf-bg))" }}
            >
                <div className="max-w-md text-center">
                    <p
                        className="mb-3 text-xs uppercase tracking-[0.28em]"
                        style={{ color: "hsl(var(--hf-gold))" }}
                    >
                        Etwas ist schiefgelaufen
                    </p>
                    <h1
                        className="mb-4 font-display italic"
                        style={{
                            color: "hsl(var(--hf-ink))",
                            fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                            lineHeight: 1.15,
                        }}
                    >
                        Die Seite konnte nicht dargestellt werden.
                    </h1>
                    <p
                        className="mb-8 text-sm md:text-base leading-relaxed"
                        style={{ color: "hsl(var(--hf-muted))" }}
                    >
                        Ein unerwarteter Fehler ist aufgetreten. Bitte laden
                        Sie die Seite neu — Ihre Daten sind unverändert.
                    </p>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        className="hf-btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-[0.14em]"
                        data-testid="app-error-boundary-reload"
                    >
                        Seite neu laden
                    </button>
                    {this.state.message && (
                        <details className="mt-8 text-left text-xs" style={{ color: "hsl(var(--hf-muted) / 0.7)" }}>
                            <summary className="cursor-pointer">Technische Details</summary>
                            <pre className="mt-2 whitespace-pre-wrap break-words">
                                {this.state.message}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}
